// wai-cart-guard.js — cart restriction ENGINE (group-agnostic).
//
// This file contains NO business rules. It knows how to:
//   - read the group declarations handed to it by snippets/wai-cart-guard.liquid
//   - work out which groups each cart line / incoming product belongs to
//   - veto a disallowed add, gate checkout, and show one notification
//
// The rules themselves live in one small snippet per group:
//   snippets/wai-guard-cool.liquid      (冷蔵便)
//   snippets/wai-guard-preorder.liquid  (予約)
// Adding, removing or disabling a group never requires touching this file.
//
// ── The rule, in one sentence ──────────────────────────────────────────────
// For every ENABLED group G: if the cart holds any G item, then every item in
// the cart must be a G item. Groups are evaluated independently and ANDed, so
// a product that belongs to two groups is handled without any precedence rule.
//
// Pure logic is exported for unit testing; the browser controller self-initializes
// only when `window` exists (so node --test can import the pure functions safely).

export const REASON = { MIX: 'mix', CAP: 'cap' };

/**
 * @typedef {{id:string, capCents:(number|null), messages:{mix:string, cap:string}}} Group
 * @typedef {{groups:Record<string,{has:boolean, hasOther:boolean}>, totalCents:number}} CartState
 */

/**
 * Derive the compact CartState used by the rules.
 * For each group we record whether the cart holds an item IN it (`has`) and
 * whether it holds an item NOT in it (`hasOther`). Both false = empty cart.
 *
 * @param {{total_price:number, items:Array<{key:string}>}} cartJson  /cart.js payload
 * @param {Map<string, Set<string>>} keysByGroup  group id → line keys in that group
 * @returns {CartState}
 */
export function buildCartState(cartJson, keysByGroup) {
  const groups = {};
  for (const [id, keys] of keysByGroup) {
    let has = false;
    let hasOther = false;
    for (const item of cartJson.items) {
      if (keys.has(item.key)) has = true;
      else hasOther = true;
    }
    groups[id] = { has, hasOther };
  }
  return { groups, totalCents: cartJson.total_price };
}

/**
 * Same CartState, built from the Liquid seed: one entry per cart line listing the
 * group ids that line belongs to. Used for the very first paint, before the async
 * /cart.js read resolves, so the checkout gate is never briefly open on a bad cart.
 *
 * @param {string[][]} lines  per cart line, the group ids it belongs to
 * @param {number} totalCents
 * @param {Group[]} groups
 * @returns {CartState}
 */
export function buildCartStateFromLines(lines, totalCents, groups) {
  const out = {};
  for (const g of groups) {
    let has = false;
    let hasOther = false;
    for (const ids of lines) {
      if (ids.includes(g.id)) has = true;
      else hasOther = true;
    }
    out[g.id] = { has, hasOther };
  }
  return { groups: out, totalCents };
}

/** Empty per-group slot, so callers never have to null-check. */
function slot(cart, id) {
  return cart.groups[id] || { has: false, hasOther: false };
}

/**
 * Mix rule, at add time. Blocks as soon as ANY enabled group would end up mixed.
 * @param {CartState} cart
 * @param {string[]} incomingGroupIds  groups the product being added belongs to
 * @param {Group[]} groups  enabled groups
 * @returns {{allowed:boolean, reason:(string|null), group:(Group|null)}}
 */
export function evaluateAdd(cart, incomingGroupIds, groups) {
  const incoming = new Set(incomingGroupIds);
  for (const g of groups) {
    const st = slot(cart, g.id);
    // Adding a G item to a cart that already holds a non-G item, or adding a
    // non-G item to a cart that already holds a G item — both split the shipment.
    if (incoming.has(g.id) ? st.hasOther : st.has) {
      return { allowed: false, reason: REASON.MIX, group: g };
    }
  }
  return { allowed: true, reason: null, group: null };
}

/**
 * Mix rule, applied to the cart as it stands (backstop for carts assembled by
 * cart permalinks or direct /cart/add.js, which never pass through the add veto).
 * @returns {Group|null} the first group that is mixed, else null
 */
export function mixViolation(cart, groups) {
  for (const g of groups) {
    const st = slot(cart, g.id);
    if (st.has && st.hasOther) return g;
  }
  return null;
}

/**
 * Cap rule: a group may declare a maximum cart total (null = no cap).
 * @returns {Group|null} the first group whose cap is exceeded, else null
 */
export function capViolation(cart, groups) {
  for (const g of groups) {
    if (g.capCents == null) continue;
    if (slot(cart, g.id).has && cart.totalCents > g.capCents) return g;
  }
  return null;
}

/**
 * The single verdict the checkout gate reads.
 * @returns {{blocked:boolean, reason:(string|null), group:(Group|null)}}
 */
export function evaluateCart(cart, groups) {
  const mixed = mixViolation(cart, groups);
  if (mixed) return { blocked: true, reason: REASON.MIX, group: mixed };
  const over = capViolation(cart, groups);
  if (over) return { blocked: true, reason: REASON.CAP, group: over };
  return { blocked: false, reason: null, group: null };
}

/**
 * Parse a `data-wai-groups` attribute value.
 * Accepts commas and/or whitespace as separators: Liquid's whitespace control makes
 * the exact spacing between per-group snippet outputs hard to guarantee, so each
 * group emits `<id>,` and we stay tolerant of either.
 */
export function parseGroupAttr(value) {
  return (value || '').split(/[\s,]+/).filter(Boolean);
}

/* ----------------------------- Browser controller ----------------------------- */

const TOAST_MS = 6000;

// The cart drawer is a modal <dialog> (showModal), painted in the browser "top layer"
// above all normal-DOM stacking contexts, so plain z-index can never sit in front of
// it (and ::backdrop dims everything below). The Popover API also renders in the top
// layer, so a popover toast can paint above the drawer — but only when it is a DOM
// descendant of that dialog (verified on Chrome 148: a body-level popover loses to the
// modal backdrop). Detect support once; unsupported browsers fall back to fixed+z-index.
const SUPPORTS_POPOVER =
  typeof HTMLElement !== 'undefined' &&
  HTMLElement.prototype &&
  typeof HTMLElement.prototype.showPopover === 'function' &&
  'popover' in HTMLElement.prototype;

// The topmost open modal dialog the toast must live under to paint above its backdrop,
// or <body> when none is open (e.g. the PDP mix block, where no drawer opens).
function topLayerHost() {
  const dialogs = document.querySelectorAll('dialog');
  for (let i = dialogs.length - 1; i >= 0; i--) {
    const d = dialogs[i];
    if (d.open && d.matches(':modal')) return d;
  }
  return document.body;
}

function ensureToast() {
  let toast = document.querySelector('.wai-cart-guard-toast');
  if (toast) return toast;
  toast = document.createElement('div');
  toast.className = 'wai-cart-guard-toast';
  toast.setAttribute('role', 'alert');
  toast.setAttribute('aria-live', 'assertive');
  // "manual" so the toast does not light-dismiss on outside click / Escape — we own
  // its lifetime via the auto-dismiss timer (transient toasts) and the ✕ button.
  if (SUPPORTS_POPOVER) toast.setAttribute('popover', 'manual');
  const text = document.createElement('span');
  text.className = 'wai-cart-guard-toast__text';
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'wai-cart-guard-toast__close';
  close.setAttribute('aria-label', 'Close');
  close.textContent = '✕';
  // The ✕ dismisses the current notice; for a persistent gate toast we also mark it
  // dismissed so the gate does not immediately re-show it (until the cart is valid
  // again or checkout is re-attempted).
  close.addEventListener('click', () => {
    toast._dismissed = true;
    hideToast(toast);
  });
  toast.append(text, close);
  return toast;
}

// Host the toast in the current top-layer owner and fade it in.
function presentToast(toast) {
  const host = topLayerHost();
  if (toast.parentNode !== host) {
    if (SUPPORTS_POPOVER && toast.matches(':popover-open')) {
      try {
        toast.hidePopover();
      } catch (_e) {
        /* not open after a morph — ignore */
      }
    }
    host.appendChild(toast);
  }
  if (SUPPORTS_POPOVER && !toast.matches(':popover-open')) {
    try {
      toast.showPopover();
    } catch (_e) {
      /* already open / detached — ignore */
    }
  }
  toast.classList.add('is-visible');
}

function hideToast(toast) {
  toast.classList.remove('is-visible');
  if (SUPPORTS_POPOVER && toast.matches(':popover-open')) {
    try {
      toast.hidePopover();
    } catch (_e) {
      /* ignore */
    }
  }
}

let toastTimer = null;

function showToast(message, reason, persistent) {
  if (!message) return;
  const toast = ensureToast();
  toast._reason = reason;
  if (!persistent) toast._dismissed = false;
  toast.querySelector('.wai-cart-guard-toast__text').textContent = message;
  presentToast(toast);
  clearTimeout(toastTimer);
  if (!persistent) toastTimer = setTimeout(() => hideToast(toast), TOAST_MS);
}

// Re-host an already-visible persistent toast after the theme morphs the cart DOM
// (which detaches the popover and closes it).
function reassertToast() {
  const toast = document.querySelector('.wai-cart-guard-toast');
  if (toast && toast.classList.contains('is-visible')) presentToast(toast);
}

function readConfig() {
  const el = document.getElementById('wai-cart-guard-config');
  const fallback = { groups: [], product: null, cart: null };
  if (!el) return fallback;
  try {
    return { ...fallback, ...JSON.parse(el.textContent || '{}') };
  } catch (_e) {
    return fallback;
  }
}

// Collect, per group, the set of cart line keys belonging to it.
function keysByGroupFromDom(groups) {
  const map = new Map();
  for (const g of groups) map.set(g.id, new Set());
  for (const row of document.querySelectorAll('[data-key][data-wai-groups]')) {
    const key = row.getAttribute('data-key');
    if (!key) continue;
    for (const id of parseGroupAttr(row.getAttribute('data-wai-groups'))) {
      if (map.has(id)) map.get(id).add(key);
    }
  }
  return map;
}

async function fetchCart() {
  const base = window.Theme?.routes?.cart_url || '/cart';
  const res = await fetch(`${base}.js`, { headers: { Accept: 'application/json' } });
  return res.json();
}

function messageFor(group, reason) {
  if (!group) return '';
  return reason === REASON.CAP ? group.messages.cap : group.messages.mix;
}

function init() {
  const config = readConfig();
  const groups = Array.isArray(config.groups) ? config.groups : [];
  if (groups.length === 0) return; // every group disabled — the guard is inert

  // Live cart snapshot, seeded synchronously from Liquid so the mix veto and the
  // checkout gate are correct before the first async /cart.js refresh resolves.
  let cart = buildCartStateFromLines([], 0, groups);
  if (config.cart && typeof config.cart.totalCents === 'number') {
    cart = buildCartStateFromLines(
      Array.isArray(config.cart.lines) ? config.cart.lines : [],
      config.cart.totalCents,
      groups
    );
  }
  // `verdict` is the single source of truth the checkout gate reads synchronously.
  const state = { verdict: evaluateCart(cart, groups) };

  async function refreshCart() {
    try {
      const json = await fetchCart();
      cart = buildCartState(json, keysByGroupFromDom(groups));
    } catch (_e) {
      /* keep previous snapshot on failure */
    }
  }

  // Show / keep / hide the persistent gate notice. Driven from applyGate so it is a
  // pure function of the gate state — re-applied after every re-render, and never
  // left hidden by a stale "valid" read (the next gate pass with fresh data corrects
  // it). Honors the shopper's ✕ dismissal until the cart is valid again.
  function syncGateToast(verdict) {
    const toast = document.querySelector('.wai-cart-guard-toast');
    if (verdict.blocked) {
      if (toast && toast._dismissed) return;
      if (toast && toast._reason === verdict.reason && toast.classList.contains('is-visible')) {
        reassertToast();
      } else {
        showToast(messageFor(verdict.group, verdict.reason), verdict.reason, true);
      }
    } else if (toast && toast._reason) {
      toast._dismissed = false; // re-arm for the next violation
      hideToast(toast);
    }
  }

  // ---- Checkout gate ----
  // Enforcement is declarative, not corrective: we never silently change the cart.
  // Instead we disable/gray every checkout affordance while the cart is invalid,
  // flag the offending lines, and show the persistent notice. Re-applied after every
  // cart re-render (the theme morphs the DOM and drops these otherwise).
  function applyGate(verdict) {
    const blocked = verdict.blocked;
    const message = messageFor(verdict.group, verdict.reason);
    for (const btn of document.querySelectorAll('button[name="checkout"], input[name="checkout"]')) {
      btn.classList.toggle('wai-cart-guard-blocked', blocked);
      if (blocked) {
        // Disable only if it isn't already (the theme renders it disabled for an empty
        // cart). Mark what we disable so unblocking restores exactly that and never
        // clears a pre-existing disabled set by the theme.
        if (!btn.disabled) {
          btn.setAttribute('disabled', '');
          btn.setAttribute('aria-disabled', 'true');
          if (message) btn.setAttribute('title', message);
          btn.setAttribute('data-wai-cart-guard-disabled', '');
        }
      } else if (btn.hasAttribute('data-wai-cart-guard-disabled')) {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
        btn.removeAttribute('title');
        btn.removeAttribute('data-wai-cart-guard-disabled');
      }
    }
    // Accelerated/dynamic checkout (Shop Pay etc.) bypasses the cart; hide it while
    // blocked. Only unhide what we hid, so a merchant-hidden widget stays hidden.
    for (const el of document.querySelectorAll(
      '.additional-checkout-buttons, .dynamic-checkout__content, [data-shopify="dynamic-checkout-cart"], .accelerated-checkout, .accelerated-checkout-block'
    )) {
      if (blocked) {
        if (!el.hasAttribute('hidden')) {
          el.setAttribute('hidden', '');
          el.setAttribute('data-wai-cart-guard-hid', '');
        }
      } else if (el.hasAttribute('data-wai-cart-guard-hid')) {
        el.removeAttribute('hidden');
        el.removeAttribute('data-wai-cart-guard-hid');
      }
    }
    // Draw the eye to the offending lines in the cart.
    for (const qty of document.querySelectorAll('.wai-cart-guard-qty-flag')) {
      qty.classList.remove('wai-cart-guard-qty-flag');
    }
    if (blocked && verdict.group) {
      for (const row of document.querySelectorAll('[data-key][data-wai-groups]')) {
        if (!parseGroupAttr(row.getAttribute('data-wai-groups')).includes(verdict.group.id)) continue;
        const qty = row.querySelector('quantity-selector-component, .quantity-selector');
        if (qty) qty.classList.add('wai-cart-guard-qty-flag');
      }
    }
    syncGateToast(verdict);
  }

  async function evaluate() {
    await refreshCart();
    state.verdict = evaluateCart(cart, groups);
    applyGate(state.verdict);
  }

  // ---- Mix-rule veto: window capture runs before the theme's document handler ----
  window.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.getAttribute('data-type') !== 'add-to-cart-form') return;

      const pf = form.closest('product-form-component');
      const incoming = parseGroupAttr(pf?.getAttribute('data-wai-groups'));

      const result = evaluateAdd(cart, incoming, groups);
      if (!result.allowed) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showToast(messageFor(result.group, REASON.MIX), REASON.MIX, false);
        return;
      }
      // Allowed add: a capped group's verdict depends on the new cart total, and a PDP
      // add may not emit cart:update — re-evaluate a few times as the add settles so
      // the gate + toast appear even when the drawer never opens.
      if (incoming.length > 0) {
        for (const ms of [400, 900, 1600]) setTimeout(evaluate, ms);
      }
    },
    { capture: true }
  );

  // ---- Bulletproof gate: block checkout intent regardless of async evaluate timing.
  // The disabled attribute is the visual gate; these capture handlers are the backstop
  // for the brief window after a re-render re-creates an un-disabled button.
  function gateCheckoutIntent(event) {
    if (!state.verdict.blocked) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showToast(messageFor(state.verdict.group, state.verdict.reason), state.verdict.reason, true);
  }
  document.addEventListener(
    'click',
    (event) => {
      const btn = event.target.closest && event.target.closest('[name="checkout"]');
      if (btn) gateCheckoutIntent(event);
    },
    { capture: true }
  );
  document.addEventListener(
    'submit',
    (event) => {
      const submitter = event.submitter;
      if (submitter && submitter.getAttribute('name') === 'checkout') gateCheckoutIntent(event);
    },
    { capture: true }
  );

  // ---- Keep the gate in sync with the live cart ----
  // Horizon 4.x announces cart changes via Storefront Standard Events
  // (StandardEvents.cartLinesUpdate from @shopify/events == 'shopify:cart:lines-update');
  // the name is inlined here so node --test can import this module without an importmap.
  // The event is dispatched BEFORE the cart fetch settles and carries a promise for it —
  // wait on it so evaluate() reads the post-update cart, not the stale one.
  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (event.promise && typeof event.promise.then === 'function') {
      event.promise.then(() => evaluate(), () => evaluate());
    } else {
      evaluate();
    }
  });
  // Legacy 2.x theme event, kept as a harmless fallback.
  document.addEventListener('cart:update', () => {
    evaluate();
  });

  // Re-check when the cart drawer opens (the cart may have changed while it was closed).
  for (const dlg of document.querySelectorAll('dialog')) {
    dlg.addEventListener('toggle', () => {
      if (dlg.open) evaluate();
    });
  }

  // The theme re-renders cart sections by morphing the DOM, dropping our gate classes
  // and dismissing the toast popover. Re-apply the current gate synchronously (no
  // flicker) on every mutation, and re-check the cart on a short debounce.
  let evalTimer = null;
  let rafPending = false;
  const observer = new MutationObserver(() => {
    if (!rafPending) {
      rafPending = true;
      requestAnimationFrame(() => {
        rafPending = false;
        applyGate(state.verdict);
      });
    }
    clearTimeout(evalTimer);
    evalTimer = setTimeout(evaluate, 200);
  });
  for (const t of document.querySelectorAll(
    'cart-drawer-component, cart-items-component, #cart-form, #cart_form, .cart-form, .shopify-cart-form'
  )) {
    observer.observe(t, { childList: true, subtree: true });
  }

  // Seed: apply the gate for the initial cart state (e.g. landing on /cart already invalid).
  applyGate(state.verdict);
  evaluate();
}

// Guarded init — only in the browser, only once.
if (typeof window !== 'undefined' && !window.__waiCartGuardInit) {
  window.__waiCartGuardInit = true;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
}
